import { PageHeader } from '@/components/layout/PageHeader';
import { FlexCol } from '@/components/ui/flex';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { useChatsService } from '@/services/chat';
import { SearchBar } from '@/shared/SearchBar';
import { useDebounce } from '@uidotdev/usehooks';
import { History } from 'lucide-react';
import { useState, type FC } from 'react';
import { ChatCard } from './components/ChatCard';
import { EmptyHistory } from './components/EmptyHistory';
import { EmptySearchResultHistory } from './components/EmptySearchResultHistory';

export const HistoryPage: FC = () => {
  const [paginationState, setPaginationState] = useState<{
    page: number;
    limit: number;
  }>({
    page: 1,
    limit: 10,
  });

  const [searchString, setSearchString] = useState<string>('');
  const debouncedSearchString = useDebounce(searchString, 300);

  const updateSearchField = (value: string) => {
    setPaginationState(prev => ({
      ...prev,
      page: 1,
    }));
    setSearchString(value);
  };

  const { chats, pagination, isPending } = useChatsService(
    paginationState.page,
    paginationState.limit,
    debouncedSearchString || undefined
  );

  const goToPage = (page: number) => {
    setPaginationState({
      page,
      limit: paginationState.limit,
    });
  };

  const handlePreviousClick = () => {
    if (pagination?.hasPrev) {
      goToPage(pagination.page - 1);
    }
  };
  const handleNextClick = () => {
    if (pagination?.hasNext) {
      goToPage(pagination.page + 1);
    }
  };

  const isSearching =
    debouncedSearchString && debouncedSearchString.trim().length > 0;
  const isEmpty = chats.length === 0;

  return (
    <FlexCol gap="6" w="full" h="full" p="6" className="max-w-[1600px]">
      <PageHeader title="History" subtitle="All chats in this workspace">
        <History size={22} />
      </PageHeader>

      <SearchBar
        searchString={searchString}
        setSearchString={updateSearchField}
        placeholder="Search..."
        className="w-[400px]"
      />

      {isEmpty && !isPending ? (
        <FlexCol
          alignItems="center"
          justifyContent="center"
          gap="4"
          className="flex-1 py-16"
        >
          {isSearching ? (
            <EmptySearchResultHistory
              onClearSearch={() => setSearchString('')}
            />
          ) : (
            <EmptyHistory />
          )}
        </FlexCol>
      ) : (
        <>
          <FlexCol alignItems="center">
            {chats.map(chat => (
              <ChatCard
                key={chat.id}
                id={chat.id}
                title={chat.title ?? 'Untitled Chat'}
                createdAt={chat.createdAt}
              />
            ))}
          </FlexCol>
          {pagination?.totalPages && pagination?.totalPages > 1 && (
            <Pagination>
              <PaginationContent>
                <PaginationItem onClick={handlePreviousClick}>
                  <PaginationPrevious>Previous</PaginationPrevious>
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink>{pagination?.page}</PaginationLink>
                </PaginationItem>
                <PaginationItem onClick={handleNextClick}>
                  <PaginationNext>Next</PaginationNext>
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </>
      )}
    </FlexCol>
  );
};
